import React, { useEffect, useState } from "react";
import { useOutletContext, Link, useNavigate } from "react-router-dom";
import { portalApi, formatPortalError } from "@/api/portalClient";
import {
  Loader2, Wallet, Receipt, Calendar, TrendingUp, ChevronRight, Banknote,
  UtensilsCrossed, Users, FileText, Calculator, UserCheck, FileSpreadsheet,
  Package, FolderOpen, Truck, Car, Wrench, Sparkles, HeartHandshake,
  Briefcase, ListChecks, Video, Hammer, Building2, ShieldCheck, FileBarChart,
  Square, LayoutGrid, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

const ICONS = {
  UtensilsCrossed, Users, FileText, Calculator, UserCheck, FileSpreadsheet,
  Package, FolderOpen, Truck, Car, Wrench, Banknote, Sparkles, HeartHandshake,
  Briefcase, ListChecks, Video, Hammer, Building2, ShieldCheck, FileBarChart,
  Receipt, Calendar, Square,
};

const ACCESS_LABEL = {
  view: "Vain katselu",
  partial: "Osittaiset oikeudet",
  full: "Täydet oikeudet",
};

const eur = (n) => new Intl.NumberFormat("fi-FI", { style: "currency", currency: "EUR" }).format(Number(n || 0));

export default function EmployeePortalDashboard() {
  const { me, modules = [], isStaffUser, hasPayroll } = useOutletContext();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const calls = [portalApi.get("/employee-portal/me/stats")];
        if (hasPayroll !== false) {
          calls.push(portalApi.get("/employee-portal/me/shifts"));
          calls.push(portalApi.get("/employee-portal/me/payslips"));
        }
        const results = await Promise.all(calls);
        setStats(results[0].data);
        setShifts(results[1]?.data || []);
        setPayslips(results[2]?.data || []);
      } catch (e) { toast.error(formatPortalError(e)); }
      setLoading(false);
    })();
  }, [hasPayroll]);

  if (loading) return <div className="text-center py-12 text-white/40"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>;

  const upcomingShifts = shifts.filter(s => new Date(s.start_at) > new Date()).slice(0, 5);
  const showPayrollSection = hasPayroll !== false;
  const showModulesSection = modules.length > 0;

  return (
    <div className="space-y-6" data-testid="emp-portal-dashboard">
      <div>
        <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300">Hei {me?.first_name}</p>
        <h1 className="text-3xl font-display gradient-text-luxe mt-1">Tervetuloa portaaliin</h1>
        <p className="text-sm text-white/50 mt-1">
          {isStaffUser
            ? "Tässä sovellukset ja moduulit joihin sinulla on pääsy."
            : "Tässä yhteenveto palkoistasi ja työvuoroistasi."}
        </p>
      </div>

      {/* Phase 17C: Moduulit joihin staff:lla on pääsy */}
      {showModulesSection && (
        <section data-testid="emp-portal-modules-section">
          <div className="flex items-center gap-2 mb-3">
            <LayoutGrid className="w-4 h-4 text-amber-300" />
            <h2 className="text-sm uppercase tracking-widest text-white/60">Sovellukset</h2>
            <span className="text-[10px] text-white/40">{modules.length} kpl</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="emp-portal-modules-grid">
            {modules.map((m) => {
              const Icon = ICONS[m.icon] || Square;
              return (
                <button
                  key={m.key}
                  onClick={() => navigate(m.path)}
                  className="group relative rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-amber-400/30 p-4 text-left transition"
                  data-testid={`emp-portal-module-card-${m.key}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${m.color} flex items-center justify-center shadow-lg`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium text-sm">{m.label}</p>
                      <p className="text-[10px] text-white/40 mt-0.5 truncate">
                        {ACCESS_LABEL[m.access_level] || m.access_level}
                        {m.role && ` · ${m.role}`}
                      </p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-amber-300 group-hover:translate-x-0.5 transition flex-shrink-0" />
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Staff_user ilman moduuleja → ohjeviesti */}
      {isStaffUser && !showModulesSection && (
        <div className="rounded-2xl border border-amber-400/20 bg-amber-500/[0.04] p-5 text-center" data-testid="emp-portal-no-modules">
          <Sparkles className="w-8 h-8 text-amber-300 mx-auto mb-2" />
          <p className="text-sm text-white/80">Et ole vielä saanut pääsyä yhteenkään moduuliin.</p>
          <p className="text-[12px] text-white/50 mt-1">Pyydä pääkäyttäjää myöntämään moduuli-oikeuksia henkilöstökortilla.</p>
        </div>
      )}

      {/* KPI – vain jos payroll-tiedot */}
      {showPayrollSection && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard icon={<Wallet className="w-4 h-4" />} label={`Vuosibrutto ${stats?.year}`} value={eur(stats?.ytd_gross || 0)} accent="text-blue-300" />
          <KpiCard icon={<Banknote className="w-4 h-4" />} label="Vuosinetto" value={eur(stats?.ytd_net || 0)} accent="text-emerald-300" />
          <KpiCard icon={<Receipt className="w-4 h-4" />} label="Palkkalaskelmia" value={stats?.payslips_count || 0} />
          <KpiCard icon={<Calendar className="w-4 h-4" />} label="Vuoroja viim. 30 pv" value={stats?.shifts_last_30_days || 0} accent="text-amber-300" />
        </div>
      )}

      {showPayrollSection && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Upcoming shifts */}
          <Panel
            title="Tulevat vuorot"
            action={<Link to="/employee-portal/shifts" className="text-xs text-amber-300 hover:text-amber-200 inline-flex items-center gap-1">Kaikki <ChevronRight className="w-3 h-3" /></Link>}
          >
            {upcomingShifts.length === 0 ? (
              <p className="text-sm text-white/40 italic py-4 text-center">Ei tulevia vuoroja</p>
            ) : (
              <div className="divide-y divide-white/5">
                {upcomingShifts.map(s => (
                  <div key={s.id} className="py-2.5 flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{s.title || s.job_name || s.route_name || "Työvuoro"}</p>
                      <p className="text-[11px] text-white/40">{s.start_at?.slice(0,10)} klo {s.start_at?.slice(11,16)} – {s.end_at?.slice(11,16)} {s.location && `· ${s.location}`}</p>
                    </div>
                    <span className="text-sm font-mono text-emerald-300">{s.hours} h</span>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          {/* Recent payslips */}
          <Panel
            title="Viimeisimmät palkkalaskelmat"
            action={<Link to="/employee-portal/payslips" className="text-xs text-amber-300 hover:text-amber-200 inline-flex items-center gap-1">Kaikki <ChevronRight className="w-3 h-3" /></Link>}
          >
            {payslips.length === 0 ? (
              <p className="text-sm text-white/40 italic py-4 text-center">Ei vielä palkkalaskelmia</p>
            ) : (
              <div className="divide-y divide-white/5">
                {payslips.slice(0, 5).map(p => (
                  <Link key={p.id} to="/employee-portal/payslips" className="py-2.5 flex items-center justify-between hover:bg-white/[0.02] -mx-2 px-2 rounded">
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate"><span className="font-mono text-white/50 text-xs">{p.payslip_number}</span> · {p.pay_period_start?.slice(0,7)}</p>
                      <p className="text-[11px] text-white/40">Maksupäivä {p.pay_date}</p>
                    </div>
                    <span className="text-sm font-mono text-emerald-300 font-semibold">{eur(p.net_total)}</span>
                  </Link>
                ))}
              </div>
            )}
          </Panel>
        </div>
      )}
    </div>
  );
}

function KpiCard({ icon, label, value, accent = "text-white" }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
      <div className="flex items-center gap-2 text-white/40">{icon}<span className="text-[10px] uppercase tracking-widest">{label}</span></div>
      <p className={`text-2xl font-display mt-2 ${accent}`}>{value}</p>
    </div>
  );
}

function Panel({ title, action, children }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-display text-white">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}
